import React, { useEffect, useRef, useState } from "react";

/**
 * Count-up animation for stat numbers.
 *
 * - Animates from 0 → `value` over `duration` ms once the element is at least
 *   30% in the viewport (IntersectionObserver).
 * - `prefix` and `suffix` strings let us wrap units like "d" or non-numeric
 *   tokens around the animated number (e.g. "1d", "+12").
 * - Respects `prefers-reduced-motion`: if the user opted out of motion,
 *   we skip animation and render the final value immediately.
 * - Uses easeOutCubic so the final third of the animation feels deliberate.
 */
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export const CountUp = ({
  value,
  prefix = "",
  suffix = "",
  duration = 1500,
  className = "",
  testid,
}) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (typeof value !== "number" || value < 0) {
      setDisplay(value);
      return undefined;
    }

    // Accessibility: skip animation when the user prefers reduced motion.
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setDisplay(value);
      return undefined;
    }

    const node = ref.current;
    if (!node) return undefined;

    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      let rafId;
      const tick = (now) => {
        const elapsed = now - t0;
        const progress = Math.min(1, elapsed / duration);
        const eased = easeOutCubic(progress);
        setDisplay(Math.round(value * eased));
        if (progress < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafId);
    };

    // If IntersectionObserver isn't available (very old browser / SSR),
    // start animation right away.
    if (typeof IntersectionObserver === "undefined") {
      const cancel = start();
      return cancel;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            start();
            observer.unobserve(node);
            break;
          }
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} data-testid={testid} className={className}>
      {prefix}
      {display.toLocaleString("nl-NL")}
      {suffix}
    </span>
  );
};

export default CountUp;
